import { Navigate } from 'react-router-dom';
import { DASHBOARD_ROUTE, resolveLandingRoute } from '@efacture/core/sector';
import { useAuth } from '@/contexts/AuthContext';
import { roleUiCodes } from '@/lib/role-ui-codes';
import { usePermissions } from '@/hooks/usePermissions';
import { useSectorProfile } from '@/api/queries/sector-profile';
import { useOnboardingState } from '@/api/queries/onboarding';
import { useTranslation } from '@/lib/i18n';

/**
 * The index route: sends the user where their role comes to work.
 *
 * Resolution happens HERE rather than at login because the sector profile
 * is fetched after authentication; the login screen cannot know it. The
 * query is the one `AppLayout` already holds, so this shares its cache
 * instead of issuing a second request.
 *
 * I-RP1 — `hasPermission` is the very function `ProtectedRoute` gates on,
 * so a resolved route is one this user can actually open.
 * I-RP2 — while the profile is loading nothing is decided, and a failed
 * or empty profile falls to `/dashboard`, which is where everyone landed
 * before roles had a say.
 *
 * A user who can complete the start-up assistant (tenant:settings) lands on
 * it, /bienvenue, until it is marked done; a failed read of its state
 * changes nothing.
 */
export function LandingRedirect() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const { data, isLoading } = useSectorProfile();
  const canCompleteOnboarding = hasPermission('tenant:settings');
  const onboarding = useOnboardingState({ enabled: canCompleteOnboarding });

  if (isLoading || (canCompleteOnboarding && onboarding.isLoading)) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (canCompleteOnboarding && onboarding.data && onboarding.data.completedAt === null) {
    return <Navigate to="/bienvenue" replace />;
  }

  // A failed query leaves no data, and no profile means no branching:
  // I-RP2 sends the user exactly where they went before roles had a say.
  const profile = data?.profile ?? null;
  const roleCodes = roleUiCodes(user?.roles ?? []);
  const route = resolveLandingRoute(roleCodes, profile, hasPermission);

  return <Navigate to={route || DASHBOARD_ROUTE} replace />;
}

export default LandingRedirect;
